<!--
    This component is used to display a button that opens a dialog with the manual.
    The manual is a markdown file that is downloaded from the CDN.
    The markdown file is converted to HTML using showdown.

    The button is used to open the manual in a dialog.
    The button is only visible if the manual is available.
 -->

<template>
  <!-- Dialog -->
  <SmDialog
    v-model:visibility="manualVisible"
    :title="markdownContent?.headline || i18n.t('general.manual')"
    size="large">
    <div style="max-height: 60vh; overflow: auto">
      <MarkdownContent
        v-if="manual && status === 'done'"
        ref="markdownContent"
        :markdown="manual" />
    </div>
  </SmDialog>

  <!-- Button -->
  <div>
    <!-- If the manual is available -->
    <div v-if="status === 'done'">
      <SmTooltip
        v-if="manual"
        :content="i18n.t('general.manual')"
        placement="top">
        <CircleHelp
          v-if="manual"
          class="mx-5 cursor-pointer text-primary"
          :size="20"
          @click="manualVisible = true" />
      </SmTooltip>
    </div>
    <!-- If the manual is loading/checked if exists -->
    <div v-else-if="status === 'loading'"></div>
    <!-- If the manual couldn't be found/not exists -->
    <div v-else-if="status === 'error'"></div>
  </div>
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n'
  import { CircleHelp } from 'lucide-vue-next'

  const i18n = useI18n()

  const manual = ref<string | null>(null) // The manual as markdown

  // done = manual is available
  // loading = manual is loading
  // error = manual couldn't be found
  const status = ref<'loading' | 'done' | 'error'>('loading')

  const manualVisible = ref(false)
  const markdownContent = ref()

  const props = defineProps({
    applicationName: {
      type: String,
      required: true,
    },
    applicationType: {
      type: String,
      required: true,
    },
  })

  onMounted(async () => {
    manual.value = await loadManual()
  })

  // Load the manual from the CDN
  async function loadManual() {
    // Download the SDK https://cdn.saasmetrix.io/Anleitungen/15FiveToken.md with fetch
    const baseUrl = 'https://cdn.saasmetrix.io/Anleitungen'

    const specificManualUrl = `${baseUrl}/${encodeURIComponent(props.applicationName)}_${encodeURIComponent(props.applicationType)}.md`

    const fallbackUrl = `${baseUrl}/default_oauth.md`

    const specificManual = await tryFetchManual(specificManualUrl)

    // If specific manual exists, use it
    if (specificManual) {
      status.value = 'done'
      return specificManual
    }

    // If specific manual doesn't exist AND it is OAuth then load fallback
    if (props.applicationType === 'oauth') {
      const fallbackManual = await tryFetchManual(fallbackUrl)

      if (fallbackManual) {
        status.value = 'done'
        return fallbackManual
      }
    }

    status.value = 'error'
    return null
  }

  async function tryFetchManual(url: string): Promise<string | null> {
    try {
      const response = await fetch(url)

      if (!response.ok) return null

      return await response.text()
    } catch (error) {
      console.error(`Failed to fetch manual: ${url}`, error)
      return null
    }
  }
</script>
